import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import apiClient from '../api/client';
import { useAppInfo } from '../context/AppContext';
import { useSite } from '../context/SiteContext';
import './AuthPage.css';

type Status = 'validating' | 'valid' | 'invalid' | 'submitting' | 'done';

function EyeIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} width={20} height={20}>
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

function PasswordField({
  value,
  onChange,
  autoComplete,
}: {
  value: string;
  onChange: (v: string) => void;
  autoComplete: string;
}) {
  const [visible, setVisible] = useState(false);
  return (
    <div className="auth-input-wrap">
      <input
        className="auth-input"
        type={visible ? 'text' : 'password'}
        autoComplete={autoComplete}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required
      />
      <button
        type="button"
        className="auth-eye"
        onMouseDown={() => setVisible(true)}
        onMouseUp={() => setVisible(false)}
        onMouseLeave={() => setVisible(false)}
        onTouchStart={() => setVisible(true)}
        onTouchEnd={() => setVisible(false)}
        tabIndex={-1}
        aria-label="Hold to reveal password"
      >
        <EyeIcon />
      </button>
    </div>
  );
}

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { logoUrl: appLogoUrl } = useAppInfo();
  const { site } = useSite();
  const logoUrl = site?.logoUrl ?? appLogoUrl;
  const token = searchParams.get('token') ?? '';

  const [status, setStatus] = useState<Status>('validating');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [formError, setFormError] = useState('');

  useEffect(() => {
    if (!token) {
      navigate('/login', { replace: true });
      return;
    }
    apiClient
      .post('/PasswordRecovery/validate-token', JSON.stringify(token), {
        headers: { 'Content-Type': 'application/json' },
      })
      .then((res) => {
        const valid = res.data?.data === true;
        setStatus(valid ? 'valid' : 'invalid');
      })
      .catch(() => setStatus('invalid'));
  }, [token, navigate]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setFormError('');

    if (password !== confirmPassword) {
      setFormError('Passwords do not match.');
      return;
    }
    if (password.length < 6) {
      setFormError('Password must be at least 6 characters.');
      return;
    }

    setStatus('submitting');
    try {
      await apiClient.post('/PasswordRecovery/reset', { token, newPassword: password });
      setStatus('done');
      setTimeout(() => navigate('/login', { replace: true }), 2500);
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        'Something went wrong. Please request a new reset link.';
      setFormError(message);
      setStatus('valid');
    }
  }

  if (status === 'validating') {
    return (
      <div className="auth-page">
        {logoUrl && <img src={logoUrl} alt="Logo" className="auth-page-logo" />}
        <div className="auth-card">
          <p style={{ textAlign: 'center', color: 'rgba(255,255,255,0.6)' }}>Validating your link…</p>
        </div>
      </div>
    );
  }

  if (status === 'invalid') {
    return (
      <div className="auth-page">
        {logoUrl && <img src={logoUrl} alt="Logo" className="auth-page-logo" />}
        <div className="auth-card">
          <h1 className="auth-title">Link Expired</h1>
          <p style={{ textAlign: 'center', color: 'rgba(255,255,255,0.7)', lineHeight: 1.6 }}>
            This reset link is invalid or has expired.
          </p>
          <p className="auth-footer">
            <Link to="/forgotpassword">Request a new link</Link>
          </p>
        </div>
      </div>
    );
  }

  if (status === 'done') {
    return (
      <div className="auth-page">
        {logoUrl && <img src={logoUrl} alt="Logo" className="auth-page-logo" />}
        <div className="auth-card">
          <h1 className="auth-title">Password Updated!</h1>
          <p style={{ textAlign: 'center', color: 'rgba(255,255,255,0.7)' }}>
            Redirecting you to sign in…
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-page">
      {logoUrl && <img src={logoUrl} alt="Logo" className="auth-page-logo" />}
      <div className="auth-card">
        <h1 className="auth-title">Set New Password</h1>
        <form onSubmit={handleSubmit} className="auth-form">
          <label className="auth-label">
            New Password
            <PasswordField value={password} onChange={setPassword} autoComplete="new-password" />
          </label>
          <label className="auth-label">
            Confirm Password
            <PasswordField value={confirmPassword} onChange={setConfirmPassword} autoComplete="new-password" />
          </label>
          {formError && <p className="auth-error">{formError}</p>}
          <button className="auth-btn" type="submit" disabled={status === 'submitting'}>
            {status === 'submitting' ? 'Saving…' : 'Update Password'}
          </button>
        </form>
      </div>
    </div>
  );
}
